import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { 
  ArrowLeft, 
  Lock, 
  Trophy,
  Home,
  User
} from 'lucide-react';
import AnnualChestGame from '../../components/game/AnnualChestGame';
import api from '../../lib/axios';

const MaProgressionCoffrePage = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('game_token');
      if (!token) {
        // Redirect to player space if not authenticated
        navigate('/mon-espace-joueur');
        return;
      }

      try {
        const res = await api.get(`/game/players/me?token=${token}`);
        if (res.data) {
          setPlayer(res.data);
          setIsAuthenticated(true);
        }
      } catch (err) {
        console.error('Auth error:', err);
        localStorage.removeItem('game_token');
        navigate('/mon-espace-joueur');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-red-950 to-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-red-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-red-950 to-black">
      {/* Header */}
      <header className="border-b border-red-800/30 bg-black/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3">
          {/* Top row - Navigation */}
          <div className="flex items-center justify-between mb-2">
            <Link to="/mon-espace-joueur">
              <Button variant="ghost" size="sm" className="text-red-400 hover:text-white hover:bg-red-900/30">
                <ArrowLeft className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Mon Espace Joueur</span>
                <span className="sm:hidden">Retour</span>
              </Button>
            </Link>
            
            {player && (
              <div className="flex items-center gap-2">
                <div className="text-right">
                  <p className="text-white font-medium text-sm">{player.first_name}</p>
                  <p className="text-red-400 text-xs">{player.total_score || 0} pts</p>
                </div>
                {player.avatar_url ? (
                  <img 
                    src={player.avatar_url} 
                    alt="Avatar" 
                    className="w-8 h-8 rounded-full object-cover border-2 border-red-500/50"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-red-600 to-red-800 flex items-center justify-center text-white font-bold text-sm">
                    {player.first_name?.[0]?.toUpperCase()}
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Title row */}
          <div className="flex items-center justify-center gap-2">
            <Lock className="w-5 h-5 text-red-400" />
            <h1 className="text-lg sm:text-xl font-bold text-white">Ma Progression - Le Coffre MSC {currentYear}</h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Quick Navigation */}
        <div className="flex flex-wrap gap-3 mb-6">
          <Link to="/mon-espace-joueur">
            <Button variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10">
              <User className="w-4 h-4 mr-2" />
              Mon Espace
            </Button>
          </Link>
          <Link to="/le-coffre-msc">
            <Button variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10">
              <Trophy className="w-4 h-4 mr-2" />
              Présentation du Jeu
            </Button>
          </Link>
          <Link to="/">
            <Button variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10">
              <Home className="w-4 h-4 mr-2" />
              Retour au Site
            </Button>
          </Link>
        </div>

        {/* Annual Chest Game Component */}
        <Card className="bg-gradient-to-br from-black/80 to-red-950/30 border-red-500/30 shadow-2xl shadow-red-500/10">
          <CardContent className="p-6">
            <AnnualChestGame year={currentYear} isAuthenticated={isAuthenticated} />
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-red-800/30 mt-12">
        <div className="max-w-6xl mx-auto text-center text-white/40 text-sm">
          <p>© {currentYear} MSC L'Agence - Le Coffre MSC</p>
        </div>
      </footer>
    </div>
  );
};

export default MaProgressionCoffrePage;
